import { config } from '@/apollo/config';
import CustomModal from '@/components/ui/customModal';
import CustomRadioButton from '@/components/ui/radio-button';
import {
  PaymentTabEnum,
  useDraftOrderUpdateMutation,
  UserDraftOrdersQuery,
} from '@/graphql';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import { IoCloseOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { Button, Modal, SelectPicker } from 'rsuite';
import styles from './styles.module.scss';

type Props = {
  open: any;
  close: () => void;
  data: UserDraftOrdersQuery | undefined;
};

const CustomArrow = () => {
  return <BsChevronDown size={20} color="#333" />;
};

export default function CartModal({ open, close, data }: Props) {
  const [updateDraftOrder] = useDraftOrderUpdateMutation();
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const orderId = data?.userDraftOrders?.[0]?.id;
  const installmentOptions = useMemo(() => {
    return data?.userDraftOrders?.[0]?.installmentsWithoutIntegration || [];
  }, [data]);
  const [selectedMonths, setSelectedMonths] = useState<{
    [key: string]: number | undefined;
  }>({});
  const [selectedPartner, setSelectedPartner] = useState<string | null>(
    installmentOptions[0]?.partner?.id || null
  );
  const t = useTranslations('Components.installments');
  const handleMonthChange = (monthAmount: number | null, partnerId: string) => {
    setSelectedMonths(prevState => {
      const newState = { ...prevState };
      if (monthAmount === undefined || monthAmount === null) {
        delete newState[partnerId];
      } else {
        newState[partnerId] = monthAmount;
      }
      return newState;
    });
  };

  const calculatePrice = (
    totalPrice: number | undefined,
    monthsAmount: number | null | undefined
  ) => {
    if (!totalPrice || !monthsAmount) return '0';
    const pricePerMonth = totalPrice / monthsAmount;
    return pricePerMonth.toLocaleString(undefined, {
      minimumFractionDigits: 0,
    });
  };

  const handleSubmit = async () => {
    if (!selectedPartner) {
      return;
    }

    const partnerOption = installmentOptions.find(
      opt => opt?.partner.id === selectedPartner
    );

    if (
      !partnerOption ||
      !partnerOption.months ||
      partnerOption.months.length === 0
    ) {
      return;
    }

    const defaultMonthValue = partnerOption.months[0]?.month;
    const monthForSelectedPartner =
      selectedMonths[selectedPartner] ?? defaultMonthValue;

    setIsSubmitting(true);

    try {
      const result = await updateDraftOrder({
        variables: {
          id: orderId,
          input: {
            ...(selectedPartner && {
              installmentPartnerName: partnerOption.partner.externalPartnerName,
              installmentPeriod: monthForSelectedPartner,
              paymentMethodName: PaymentTabEnum.INSTALLMENT,
            }),
          },
        },
      });

      if (result.data?.draftOrderUpdate?.order?.id) {
        router.push(`/order`);
      } else {
        toast.error(
          `Ошибка при создании заказа (логическая):, ${result.errors} || Нет данных заказа`
        );
      }
    } catch (error) {
      toast.error(`Ошибка при выполнении мутации createDraftOrder:, ${error}`);
    } finally {
      setIsSubmitting(false);
      close();
    }
  };

  return (
    <CustomModal
      closeOnOutsideClick
      open={open}
      close={close}
      className={styles.modalMap}
    >
      <div className={styles.header}>
        <Modal.Title className={styles.headerTitle}>
          {t('choose-partner')}
        </Modal.Title>
        <IoCloseOutline onClick={close} className={styles.headerClose} />
      </div>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.installmentsWrapper}>
          <div className={styles.installments}>
            <div className={styles.headers}>
              <p className={styles.headersText}>{t('partners')}</p>
              <p className={styles.headersText}>{t('period')}</p>
              <p className={styles.headersText}>{t('per-month')}</p>
              <p className={styles.headersText}>{t('total')}</p>
            </div>

            <div className={styles.selectBanks}>
              {installmentOptions.map(option => {
                if (!option) {
                  return null;
                }
                const { partner, months } = option;
                const partnerId = partner?.id;

                const monthsData =
                  months?.map(month => ({
                    label: `${month?.month} ${t('month')}`,
                    value: month?.month,
                    totalPrice: month?.price?.amount,
                  })) || [];

                if (monthsData.length === 0) {
                  return null;
                }

                const defaultMonth = monthsData[0]?.value;
                const selectedMonth = selectedMonths[partnerId] || defaultMonth;

                const selectedMonthData = monthsData.find(
                  month => month.value === selectedMonth
                );

                const totalPrice = selectedMonthData?.totalPrice;
                const perMonth = calculatePrice(totalPrice, selectedMonth);

                return (
                  <div
                    key={option.partner.id}
                    className={`${styles.bankCard} ${
                      selectedPartner === option.partner.id
                        ? styles.bankCardActive
                        : styles.bankCard
                    }`}
                    onClick={() => setSelectedPartner(partnerId)}
                  >
                    <div className={styles.checkBox}>
                      <CustomRadioButton
                        label={partner?.logo ? '' : partner?.name}
                        checked={selectedPartner === partnerId}
                        onChange={() => setSelectedPartner(partnerId)}
                        value={partnerId}
                      />
                      {partner?.logo && (
                        <Image
                          src={`${config.publicApiUrl}${partner.logo}`}
                          alt={partner?.name}
                          width={99}
                          height={32}
                          objectFit="contain"
                        />
                      )}
                    </div>

                    <div className={styles.selectBox}>
                      <p className={styles.wrapperText}>{t('period')}</p>
                      <div className={styles.select}>
                        <SelectPicker
                          data={monthsData}
                          caretAs={CustomArrow}
                          appearance="subtle"
                          defaultValue={defaultMonth}
                          cleanable={false}
                          searchable={false}
                          onChange={value =>
                            handleMonthChange(value, partnerId)
                          }
                          value={selectedMonth}
                          // style={{ width: 150 }}
                        />
                      </div>
                    </div>

                    <div className={styles.wrapper}>
                      <p className={styles.wrapperText}>{t('per-month')}</p>
                      <p className={styles.price}>
                        {perMonth} {t('price-month')}
                      </p>
                    </div>
                    <div className={styles.wrapper}>
                      <p className={styles.wrapperText}>{t('total')}</p>
                      <p className={styles.totalPrice}>
                        {totalPrice?.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                        })}{' '}
                        {t('price')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={styles.wrapperBtn}>
            <Button
              type="button"
              onClick={() => close()}
              className={styles.footerButton2}
            >
              {t('go-back')}
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit()}
              className={styles.footerButton1}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t('go-to-installment')}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </CustomModal>
  );
}
